import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import { formatCurrency } from 'tio-ui/format';
import Chip from 'tio-ui/components/chip';
import { or } from 'tio-ui/utilities';
import ContributionHero from 'tio-employee/components/slr/contribution-hero';
import {
  PhSparkle,
  PhPercent,
  PhHandshake,
  PhCheckCircle,
  PhWarningCircle,
  PhLinkBreak,
  PhShieldSlash,
  PhMoneyWavy,
  PhProhibit,
} from 'ember-phosphor-icons';
import {
  getCurrentDemoClientData,
  getCurrentDemoScenario,
  getCurrentDemoDebtLevel,
} from 'tio-employee/utils/career-pathways-demo-data';
import type RouterService from '@ember/routing/router-service';
import type CareerPathwaysApiService from 'tio-employee/services/career-pathways-api';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type SessionContextService from 'tio-employee/services/session-context';
import { t, type IntlService } from 'ember-intl';
import type { LoanAccount } from 'tio-employee/components/reimagined/types';
import type { DebtMilestone } from 'tio-employee/components/reimagined/milestone-tracker';
import type { SmartAction } from 'tio-employee/components/reimagined/smart-action-card';
import type { EmployerContributions } from 'tio-employee/services/career-pathways-api';
import {
  type DataSourceInfo,
  type LiveSection127Info,
  type ContributionHistoryData,
  type ContributionActionItem,
  type ContributionYearlyPayment,
  type SlrPageState,
  normalizeSlrPageState,
  getActionItemIconBg as actionItemIconBg,
  getActionItemIconColor as actionItemIconColor,
  SLR_ALERTS_UNAVAILABLE_ACTION_ID,
} from 'tio-employee/services/reimagined-data';
import DataIndicator from 'tio-employee/components/reimagined/data-indicator';
import type { DataSource } from 'tio-employee/components/reimagined/data-indicator';
import AccountSelectorDrawer from 'tio-employee/components/slr/account-selector-drawer';
import DocumentUploadDrawer from 'tio-employee/components/slr/document-upload-drawer';
import BenefitDetailDrawer from 'tio-employee/components/slr/benefit-detail-drawer';
import IssueNotificationCard from 'tio-employee/components/slr/issue-notification-card';
import AddressSelectionCard from 'tio-employee/components/slr/address-selection-card';
import GroupedIssueNotificationCard from 'tio-employee/components/slr/grouped-issue-notification-card';
import type { AccountExtension as AccountModel } from 'tio-data/data/resources/account.ext';
import type { PostalAddressExtension as PostalAddressModel } from 'tio-data/data/resources/postal-address.ext';
import type { Store as StoreService } from '@warp-drive/core';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import { findRecord } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';

interface LoansIndexModel {
  loans: LoanAccount[];
  hasLinkedLoans: boolean;
  isDemoMode: boolean;
  dataSourceInfo: DataSourceInfo;
  employerContributions: {
    contributions: EmployerContributions;
    isLiveData: boolean;
  };
  section127Info: LiveSection127Info;
  hasStrategyFinderResults: boolean;
  activeAccounts: AccountModel[];
  accountReceivingContributions: AccountModel | null;
  personId: string | null;
  contributionHistory?: ContributionHistoryData;
  limitsStatus?: LimitsStatusDataSignature;
}

interface LoansIndexSignature {
  Args: {
    model: LoansIndexModel;
  };
}

// Demo loan data with contribution tracking
interface LoanAccountWithContributions extends LoanAccount {
  amountReceived?: number;
  contributionCount?: number;
}

const DEMO_LOANS: LoanAccountWithContributions[] = [
  {
    id: '1',
    name: 'Federal Direct Loan',
    accountNumber: '****4821',
    servicer: 'MOHELA',
    balance: 32450,
    originalBalance: 48000,
    interestRate: 5.2,
    monthlyPayment: 285,
    nextPaymentDate: '2026-02-15',
    estimatedPayoff: '2028-03-15',
    isReceivingEmployerHelp: true,
    loanCount: 4,
    accountType: 'federal',
    paymentAddress: '123 Main St, Springfield, IL 62704',
    amountReceived: 800,
    contributionCount: 8,
  },
  {
    id: '2',
    name: 'Federal Stafford Loan',
    accountNumber: '****7293',
    servicer: 'Nelnet',
    balance: 12780,
    originalBalance: 22000,
    interestRate: 6.8,
    monthlyPayment: 102,
    nextPaymentDate: '2026-02-20',
    estimatedPayoff: '2029-08-15',
    isReceivingEmployerHelp: false,
    loanCount: 2,
    accountType: 'federal',
    paymentAddress: '456 Oak Ave, Lincoln, NE 68508',
    amountReceived: 0,
    contributionCount: 0,
  },
];

// Demo milestones
const DEMO_MILESTONES: DebtMilestone[] = [
  {
    id: '1',
    title: 'First $5k',
    description: 'Paid first $5,000',
    threshold: 7,
    achieved: true,
    achievedDate: 'Mar 2024',
    icon: 'rocket',
  },
  {
    id: '2',
    title: '25% Done',
    description: 'Quarter paid off',
    threshold: 25,
    achieved: true,
    achievedDate: 'Sep 2024',
    icon: 'star',
  },
  {
    id: '3',
    title: 'Halfway',
    description: '50% to freedom',
    threshold: 50,
    achieved: false,
    icon: 'target',
  },
  {
    id: '4',
    title: 'Under $20k',
    description: 'Below $20,000',
    threshold: 71,
    achieved: false,
    icon: 'medal',
  },
  {
    id: '5',
    title: 'Debt Free!',
    description: '$0 balance',
    threshold: 100,
    achieved: false,
    icon: 'crown',
  },
];

// Smart actions for debt management - static structure, dynamic content via getter
const createDebtActions = (options: {
  section127Remaining: number;
  section127Used: number;
  hasStrategyFinderResults: boolean;
  formatCurrency: (amount: number) => string;
}): SmartAction[] => {
  const { section127Remaining, section127Used, hasStrategyFinderResults, formatCurrency } = options;

  const actions: SmartAction[] = [];

  // Strategy Finder action - higher priority if user hasn't run it
  actions.push({
    id: '1',
    priority: hasStrategyFinderResults ? 'medium' : 'high',
    category: 'money',
    icon: PhPercent,
    title: hasStrategyFinderResults
      ? 'Update your repayment strategy'
      : 'Find your optimal repayment strategy',
    description: hasStrategyFinderResults
      ? "Life changes. Review your strategy to ensure you're still on the best path to debt freedom."
      : "Based on your employment and loan profile, we can find the best path to debt freedom—whether that's PSLF, IDR, or aggressive payoff.",
    impact: 'Potentially save thousands',
    cta: hasStrategyFinderResults ? 'Review Strategy' : 'Run Strategy Finder',
    route: 'authenticated.repaying-student-debt.repayment-strategy-finder',
  });

  // Section 127 action - only show if there's remaining balance
  if (section127Remaining > 0) {
    actions.push({
      id: '2',
      priority: section127Remaining > 1000 ? 'high' : 'medium',
      category: 'opportunity',
      icon: PhHandshake,
      title: `${formatCurrency(section127Remaining)} of Section 127 unused`,
      description: `You've used ${formatCurrency(section127Used)} of your $5,250 tax-free benefit this year. Allocating the rest to loans could accelerate payoff.`,
      impact: 'Pay off faster',
      cta: 'Optimize Allocation',
      route: 'authenticated.reimagined.finance.optimizer',
    });
  }

  // AI Advisor action
  actions.push({
    id: '3',
    priority: 'medium',
    category: 'opportunity',
    icon: PhSparkle,
    title: 'Ask the AI Loan Advisor',
    description:
      'Get personalized guidance on PSLF, IDR plans, refinancing decisions, and repayment optimization from our AI expert.',
    impact: 'Expert advice instantly',
    cta: 'Chat with Advisor',
    route: 'authenticated.reimagined.plan.advisor',
  });

  return actions;
};

export default class CareerPathwaysLoansIndex extends Component<LoansIndexSignature> {
  @service('career-pathways-api') declare careerPathwaysApi: CareerPathwaysApiService;
  @service declare reimaginedData: ReimaginedDataService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare intl: IntlService;
  @service declare store: StoreService;
  @tracked expandedAccounts = new Set<string>();
  @tracked isAccountDrawerOpen = false;
  @tracked isUploadDrawerOpen = false;
  @tracked isBenefitDrawerOpen = false;

  get person() {
    return this.sessionContext.person;
  }

  get isDemoMode() {
    return this.reimaginedData.isDemoMode;
  }

  get dataSourceInfo() {
    return this.args.model.dataSourceInfo;
  }

  /**
   * Data source for student loan sections
   */
  get studentLoansSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  /**
   * Data source for employer contributions
   */
  get contributionsSource(): DataSource {
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.employerContributions === 'live' ? 'live' : 'placeholder';
  }

  /**
   * Data source for LRAP discovery
   */
  get lrapSource(): DataSource {
    // LRAP discovery is currently demo/placeholder
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  /**
   * Data source for AI recommendations/smart actions
   */
  get recommendationsSource(): DataSource {
    // AI recommendations are placeholder until AI service built
    return this.isDemoMode ? 'demo' : 'placeholder';
  }

  /**
   * Data source for debt milestones
   */
  get milestonesSource(): DataSource {
    // Milestones are calculated from loan data
    if (this.isDemoMode) return 'demo';
    return this.dataSourceInfo.studentLoans === 'live' ? 'live' : 'placeholder';
  }

  get demoClient() {
    return getCurrentDemoClientData();
  }

  get scenario() {
    return getCurrentDemoScenario();
  }

  get showLoans() {
    return this.isDemoMode ? this.scenario.features.studentLoanRepayment : true;
  }

  get isFirstLogin() {
    return this.isDemoMode ? this.scenario.userState.isFirstLogin : false;
  }

  get hasLinkedLoans() {
    // The Debt axis owns linked-loan state in demo (orthogonal to the product
    // scenario); 'not-linked' drives the no-accounts empty state below.
    return this.isDemoMode
      ? getCurrentDemoDebtLevel().hasLinkedLoans
      : this.args.model.hasLinkedLoans;
  }

  get loans(): LoanAccountWithContributions[] {
    if (!this.isDemoMode) {
      return this.args.model.loans;
    }
    const debt = getCurrentDemoDebtLevel();
    if (!debt.hasLinkedLoans || debt.balanceDollars === null) {
      return [];
    }
    // One synthesized loan reflecting the selected debt level. originalBalance is
    // ~1.4× the current balance so progress milestones render; payoff ≈ 10 yrs out.
    return [
      {
        ...(DEMO_LOANS[0] as LoanAccountWithContributions),
        balance: debt.balanceDollars,
        originalBalance: Math.round(debt.balanceDollars * 1.4),
        monthlyPayment: debt.monthlyPaymentDollars ?? 0,
        interestRate: debt.interestRate,
      },
    ];
  }

  get milestones(): DebtMilestone[] {
    // In demo mode, use static milestones
    if (this.isDemoMode) {
      return DEMO_MILESTONES;
    }

    // Calculate milestones dynamically from real loan data
    const totalOriginal = this.totalOriginalBalance;
    const totalCurrent = this.totalBalance;

    if (totalOriginal <= 0) {
      return DEMO_MILESTONES;
    }

    const paidOff = totalOriginal - totalCurrent;
    const paidPercent = (paidOff / totalOriginal) * 100;

    return [
      {
        id: '1',
        title: 'First $5k',
        description: 'Paid first $5,000',
        threshold: totalOriginal > 5000 ? (5000 / totalOriginal) * 100 : 50,
        achieved: paidOff >= 5000,
        achievedDate: paidOff >= 5000 ? 'Achieved!' : undefined,
        icon: 'rocket',
      },
      {
        id: '2',
        title: '25% Done',
        description: 'Quarter paid off',
        threshold: 25,
        achieved: paidPercent >= 25,
        achievedDate: paidPercent >= 25 ? 'Achieved!' : undefined,
        icon: 'star',
      },
      {
        id: '3',
        title: 'Halfway',
        description: '50% to freedom',
        threshold: 50,
        achieved: paidPercent >= 50,
        icon: 'target',
      },
      {
        id: '4',
        title: '75% Done',
        description: 'Three quarters there',
        threshold: 75,
        achieved: paidPercent >= 75,
        icon: 'medal',
      },
      {
        id: '5',
        title: 'Debt Free!',
        description: '$0 balance',
        threshold: 100,
        achieved: totalCurrent <= 0,
        icon: 'crown',
      },
    ];
  }

  get section127Info() {
    return this.args.model.section127Info;
  }

  get hasStrategyFinderResults() {
    return this.isDemoMode ? false : this.args.model.hasStrategyFinderResults;
  }

  get actions() {
    // In demo mode, use demo values
    if (this.isDemoMode) {
      return createDebtActions({
        section127Remaining: 1200,
        section127Used: 4050,
        hasStrategyFinderResults: false,
        formatCurrency: formatCurrency,
      });
    }

    // Use live data
    return createDebtActions({
      section127Remaining: this.section127Info.remaining,
      section127Used: this.section127Info.yearToDateUsed,
      hasStrategyFinderResults: this.hasStrategyFinderResults,
      formatCurrency: formatCurrency,
    });
  }

  get totalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.balance, 0);
  }

  get totalOriginalBalance() {
    return this.loans.reduce((sum, loan) => sum + loan.originalBalance, 0);
  }

  get totalMonthlyPayment() {
    return this.loans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  }

  get progressPercent() {
    if (this.totalOriginalBalance <= 0) return 0;
    const paid = this.totalOriginalBalance - this.totalBalance;
    return Math.round((paid / this.totalOriginalBalance) * 100);
  }

  get yearProgressAmount() {
    if (this.isDemoMode) return 4850;
    // For live data, estimate YTD progress from employer contributions
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      // Include employer contributions as part of year progress
      return contributions.contributions.yearToDate;
    }
    const paid = this.totalOriginalBalance - this.totalBalance;
    return paid > 0 ? paid : 0;
  }

  get yearOverYearImprovement() {
    // Calculate year-over-year improvement percentage
    if (this.isDemoMode) return 34;
    // For live data, estimate based on progress
    if (this.totalOriginalBalance <= 0) return 0;
    const progressPct = this.progressPercent;
    // Estimate improvement based on how much faster we're paying off vs original schedule
    return Math.min(Math.round(progressPct * 0.5), 50);
  }

  get estimatedPayoffDate() {
    if (this.isDemoMode) return '2029-08-15';
    // Calculate from loan data
    if (this.loans.length === 0) return new Date().toISOString();
    // Find the latest estimated payoff from all loans
    const payoffDates = this.loans
      .map((loan) => loan.estimatedPayoff)
      .filter((date) => date)
      .sort((a, b) => new Date(b).getTime() - new Date(a).getTime());
    return payoffDates[0] || new Date(Date.now() + 5 * 365 * 24 * 60 * 60 * 1000).toISOString();
  }

  get averageInterestRate() {
    if (!this.loans.length) return 0;
    const weighted = this.loans.reduce((sum, loan) => sum + loan.balance * loan.interestRate, 0);
    const total = this.loans.reduce((sum, loan) => sum + loan.balance, 0);
    return total > 0 ? Number((weighted / total).toFixed(1)) : 0;
  }

  get nextPaymentSubtext() {
    if (this.isDemoMode) return 'Next due: Feb 15';
    const next = this.loans
      .map((loan) => loan.nextPaymentDate)
      .filter((date) => date)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
    if (!next) return 'Next due: —';
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
    }).format(new Date(next));
    return `Next due: ${formatted}`;
  }

  get employerName() {
    if (this.isDemoMode) return this.demoClient.client.name;
    return (
      this.sessionContext.company?.name ||
      this.sessionContext.currentEmployee?.company?.name ||
      'Your employer'
    );
  }

  /**
   * Monthly employer contribution amount - live from plan data
   */
  get monthlyContribution() {
    if (this.isDemoMode) return 125;
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return Math.round(contributions.contributions.projectedAnnual / 12) || 0;
    }
    return 0;
  }

  get annualContribution() {
    return this.monthlyContribution * 12;
  }

  get waitingPeriodDate() {
    const opts = {
      month: 'long' as const,
      day: 'numeric' as const,
      year: 'numeric' as const,
    };
    if (this.isDemoMode) {
      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + 60);
      return this.intl.formatDate(futureDate, opts);
    }
    const dateStr = this.sessionContext.currentEmployee?.eligibilityStartDate;
    return dateStr ? this.intl.formatDate(new Date(dateStr), opts) : undefined;
  }

  /**
   * Lifetime employer benefit amount - live from contribution summary
   */
  get lifetimeBenefit() {
    if (this.isDemoMode) return 18000;
    const contributions = this.args.model.employerContributions;
    if (contributions.isLiveData) {
      return contributions.contributions.lifetime || 0;
    }
    return 0;
  }

  get contributionYears(): ContributionYearlyPayment[] {
    return this.args.model.contributionHistory?.paymentsByYear ?? [];
  }

  /**
   * Action items requiring user attention — works for both demo and live users.
   *
   * In demo mode, use `?demo_alert=account-setup|account-ineligible|plan-requirement|default|
   * partially-applied|blank-account-number|no-loan-balance|blank-payment-address|all|none`
   * to preview each issue type icon.
   *
   * In live mode, action items come from the API's SlrAlertService (source of
   * truth). When the endpoint returns no alerts, only payment validation
   * errors are derived client-side; when the fetch FAILS outright, a static
   * "alerts unavailable" outage notice is added alongside them.
   *
   * Figma ref: Issue Type Icons (node 1342:5380)
   */
  get actionItems(): ContributionActionItem[] {
    if (this.isDemoMode) {
      const params = new URLSearchParams(window.location.search);
      const alertParam = params.get('demo_alert') ?? 'all';

      const allDemoAlerts: ContributionActionItem[] = [
        {
          id: 'slr-grouped-account-info',
          title: this.intl.t('slr.reimagined.alert_grouped_account_info_title'),
          description: this.intl.t('slr.reimagined.alert_grouped_account_info_desc_mixed'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'account-setup',
          tasks: [
            {
              id: 'upload-loan-information',
              title: this.intl.t('slr.reimagined.grouped_upload_task_title'),
              description: this.intl.t(
                'slr.reimagined.grouped_upload_task_desc_account_and_balance'
              ),
              resolutionType: 'upload',
              issueLabels: [
                this.intl.t('slr.reimagined.alert_blank_account_number_label'),
                this.intl.t('slr.reimagined.alert_no_loan_balance_label'),
              ],
              successMessage: this.intl.t('slr.reimagined.successfully_updated_loan_information'),
            },
            {
              id: 'blank-payment-address',
              title: this.intl.t('slr.reimagined.grouped_address_task_title'),
              description: this.intl.t('slr.reimagined.alert_blank_payment_address_desc_dropdown'),
              resolutionType: 'dropdown',
              issueLabels: [this.intl.t('slr.reimagined.alert_blank_payment_address_label')],
              metadata: {
                available_addresses: [
                  { id: 'addr-1', label: '9876 north ave City, State 98765' },
                  { id: 'addr-2', label: '1234 main st City, State 12345' },
                  { id: 'addr-3', label: '333 Washington blvd City, State 33333' },
                ],
              },
            },
          ],
          successMessage: this.intl.t('slr.reimagined.successfully_updated_loan_information'),
        },
        {
          id: 'slr-all-pif',
          title: this.intl.t('slr.reimagined.alert_all_pif_title'),
          description: this.intl.t('slr.reimagined.alert_all_pif_desc'),
          badge: this.intl.t('slr.reimagined.action_action_needed'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
          resolutionType: 'upload',
        },
        {
          id: 'slr-partially-applied',
          title: this.intl.t('slr.reimagined.alert_partially_applied_title'),
          description: this.intl.t('slr.reimagined.alert_partially_applied_desc'),
          badge: this.intl.t('slr.reimagined.action_action_needed'),
          badgeIntent: 'danger',
          route: 'authenticated.reimagined.loans',
          issueType: 'account-setup',
          resolutionType: 'contact-support',
        },
        {
          id: 'slr-blank-account-number',
          title: this.intl.t('slr.reimagined.alert_blank_account_number_title'),
          description: this.intl.t('slr.reimagined.alert_blank_account_number_desc'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
          resolutionType: 'upload',
        },
        {
          id: 'slr-no-loan-balance',
          title: this.intl.t('slr.reimagined.alert_no_loan_balance_title'),
          description: this.intl.t('slr.reimagined.alert_no_loan_balance_desc'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.observability.upload',
          issueType: 'account-setup',
          resolutionType: 'upload',
        },
        {
          id: 'slr-blank-payment-address',
          title: this.intl.t('slr.reimagined.alert_blank_payment_address_title'),
          description: this.intl.t('slr.reimagined.alert_blank_payment_address_desc_dropdown'),
          badge: this.intl.t('slr.reimagined.action_act_now'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'account-setup',
          resolutionType: 'dropdown',
          metadata: {
            available_addresses: [
              { id: 'addr-1', label: '9876 north ave City, State 98765' },
              { id: 'addr-2', label: '1234 main st City, State 12345' },
              { id: 'addr-3', label: '333 Washington blvd City, State 33333' },
            ],
          },
        },
        {
          id: 'validation-INVALID_LOAN_TYPE',
          title: this.intl.t('slr.reimagined.action_account_ineligible_title'),
          description: this.intl.t('slr.reimagined.action_account_ineligible_desc'),
          badge: this.intl.t('slr.reimagined.action_required_for_payment'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'account-ineligible',
        },
        {
          id: 'plan-ineligible',
          title: this.intl.t('slr.reimagined.action_plan_issue_title'),
          description: this.intl.t('slr.reimagined.action_plan_issue'),
          badge: this.intl.t('slr.reimagined.action_action_needed'),
          badgeIntent: 'danger',
          route: 'authenticated.observability.upload',
          issueType: 'plan-requirement',
        },
        {
          id: 'payment-error',
          title: this.intl.t('slr.reimagined.action_resolve_error_title'),
          description: this.intl.t('slr.reimagined.action_resolve_error'),
          badge: this.intl.t('slr.reimagined.action_needs_review'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'default',
          resolutionType: 'contact-support',
        },
        {
          // Static notice shown when the /slr-alerts fetch fails (outage) —
          // distinct from the endpoint returning no alerts.
          id: 'slr-alerts-unavailable',
          title: this.intl.t('slr.reimagined.action_alerts_unavailable_title'),
          description: this.intl.t('slr.reimagined.action_alerts_unavailable_desc'),
          badge: this.intl.t('slr.reimagined.action_needs_review'),
          badgeIntent: 'warning',
          route: 'authenticated.reimagined.loans',
          issueType: 'default',
        },
      ];

      if (alertParam === 'none') return [];
      if (alertParam === 'all') return allDemoAlerts;
      // Filter by issueType, resolutionType, or SLR alert type (from the item id after 'slr-')
      return allDemoAlerts.filter(
        (a) =>
          a.issueType === alertParam ||
          a.resolutionType === alertParam ||
          a.id === `slr-${alertParam}`
      );
    }

    return this.args.model.contributionHistory?.actionItems ?? [];
  }

  getActionItemIconBg = (item: ContributionActionItem) => actionItemIconBg(item);

  getActionItemIconColor = (item: ContributionActionItem) => actionItemIconColor(item);

  /**
   * Hero section metrics — demo mode uses static values,
   * live mode computes from employer contributions data.
   * Variant is determined by plan limits (annualLimit / lifetimeLimit in cents).
   */
  get heroProps() {
    if (this.isDemoMode) {
      return {
        variant: 2 as 1 | 2 | 3 | 4,
        annualUsage: 0,
        annualLimit: 0,
        annualProgressPercent: 0,
        lifetimeUsage: 4800,
        lifetimeLimit: 12000,
        lifetimeProgressPercent: 40,
        lifetimeContribution: 4800,
        lifetimeAsOfDate: '01/2023',
        yearContribution: 800,
        currentYear: new Date().getFullYear(),
        paymentCount: 4,
        lastPaymentAmount: 125,
        lastPaymentDate: '02/2026',
        nextContribution: 200,
        nextContributionDate: '01/2026',
        blocked: false,
      };
    }

    const plan = this.sessionContext.currentEmployee?.plan;
    const annualLimitCents = plan?.annualLimit ?? 0;
    const lifetimeLimitCents = plan?.lifetimeLimit ?? 0;
    const annualLimitDollars = annualLimitCents / 100;
    const lifetimeLimitDollars = lifetimeLimitCents / 100;

    const contributions = this.args.model.employerContributions;
    const isLive = contributions.isLiveData;
    const yearToDate = isLive ? contributions.contributions.yearToDate : 0;
    const lifetime = isLive ? contributions.contributions.lifetime : 0;

    const now = new Date();

    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const nextDate = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      year: 'numeric',
    }).format(nextMonth);

    let variant: 1 | 2 | 3 | 4;
    if (annualLimitCents > 0 && lifetimeLimitCents > 0) {
      variant = 4;
    } else if (annualLimitCents > 0) {
      variant = 1;
    } else if (lifetimeLimitCents > 0) {
      variant = 2;
    } else {
      variant = 3;
    }

    const annualProgressPercent =
      annualLimitDollars > 0
        ? Math.min(Math.round((yearToDate / annualLimitDollars) * 100), 100)
        : 0;
    const lifetimeProgressPercent =
      lifetimeLimitDollars > 0
        ? Math.min(Math.round((lifetime / lifetimeLimitDollars) * 100), 100)
        : 0;

    const projected = isLive ? contributions.contributions.projectedAnnual : 0;
    const monthly = Math.round(projected / 12) || 0;

    // Next contribution mirrors contribution-history's fallback order:
    // preview row, then the plan's scheduled per-cycle amount, then the
    // run-rate projection as a last resort.
    const history = this.args.model.contributionHistory;
    const previewedNext = history?.nextPayment?.amount ?? 0;
    const scheduledMonthly = history?.scheduledMonthlyAmount ?? 0;
    const nextContribution = previewedNext > 0 ? previewedNext : scheduledMonthly || monthly;

    const payments = this.args.model.contributionHistory?.payments;
    const lastPayment = payments?.length ? payments[0] : undefined;
    const lastPaymentAmount = lastPayment?.amount ?? 0;
    const lastPaymentDate = lastPayment?.paidDate
      ? (() => {
          const d = new Date(lastPayment.paidDate);
          return isNaN(d.getTime())
            ? ''
            : this.intl.formatDate(d, { month: '2-digit', year: 'numeric' });
        })()
      : '';

    return {
      variant,
      annualUsage: yearToDate,
      annualLimit: annualLimitDollars,
      annualProgressPercent,
      lifetimeUsage: lifetime,
      lifetimeLimit: lifetimeLimitDollars,
      lifetimeProgressPercent,
      lifetimeContribution: lifetime,
      lifetimeAsOfDate: this.formatOldestPaymentDate(),
      yearContribution: yearToDate,
      currentYear: now.getFullYear(),
      paymentCount: this.paymentCountForCurrentYear,
      lastPaymentAmount,
      lastPaymentDate,
      nextContribution,
      nextContributionDate: nextDate,
      blocked: this.isContributionBlocked,
    };
  }

  formatOldestPaymentDate() {
    const payments = this.args.model.contributionHistory?.payments;
    if (!payments?.length) return '';
    const oldest = payments[payments.length - 1];
    const dateStr = oldest?.paidDate;
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return this.intl.formatDate(date, {
      month: '2-digit',
      year: 'numeric',
    });
  }

  get paymentCountForCurrentYear() {
    const currentYear = String(new Date().getFullYear());
    const yearData = this.args.model.contributionHistory?.paymentsByYear?.find(
      (y) => y.year === currentYear
    );
    return yearData?.paymentCount ?? 0;
  }

  get receivingLoans() {
    return this.loans.filter((loan) => loan.isReceivingEmployerHelp);
  }

  get otherLoans() {
    return this.loans.filter((loan) => !loan.isReceivingEmployerHelp);
  }

  toggleAccountExpansion = (accountId: string) => {
    if (this.expandedAccounts.has(accountId)) {
      this.expandedAccounts.delete(accountId);
    } else {
      this.expandedAccounts.add(accountId);
    }
    this.expandedAccounts = new Set(this.expandedAccounts);
  };

  isAccountExpanded = (accountId: string) => {
    return this.expandedAccounts.has(accountId);
  };

  toggleAccountDrawer = () => {
    this.isAccountDrawerOpen = !this.isAccountDrawerOpen;
  };

  toggleUploadDrawer = () => {
    this.isUploadDrawerOpen = !this.isUploadDrawerOpen;
  };

  openBenefitDrawer = () => {
    this.isBenefitDrawerOpen = true;
  };

  closeBenefitDrawer = () => {
    this.isBenefitDrawerOpen = false;
  };

  get plan() {
    if (this.isDemoMode) {
      return {
        name: 'Acme Student Loan Repayment',
        description: '',
        cadence: 'MONTHLY',
        milestoneDate: 'ELIGIBILITY_DATE',
        waitingPeriod: 3,
        waitingPeriodUnit: 'months',
        tiers: [{ fromCycle: 1, per: 'MONTHLY', maximum: null, amount: 12500 }],
        annualLimit: 150000,
        lifetimeLimit: 1200000,
      } as unknown as import('tio-data/data/resources/plan.ext').PlanExtension;
    }
    return this.sessionContext.currentEmployee?.plan ?? null;
  }

  handleActionItemClick = (item: ContributionActionItem) => {
    if (item.resolutionType === 'upload') {
      this.isUploadDrawerOpen = true;
      return;
    }
    if (item.resolutionType === 'dropdown') {
      this.isAccountDrawerOpen = true;
    }
  };

  getAddressOptions = (item: ContributionActionItem) => {
    const addresses = (item.metadata?.available_addresses ?? []) as Array<{
      id: string;
      label: string;
    }>;
    return addresses;
  };

  handleAddressSubmit = async (_item: ContributionActionItem, addressId: string) => {
    if (this.isDemoMode) return;

    const account = this.args.model.accountReceivingContributions;
    if (!account) throw new Error('No contribution account found');

    // WarpDrive builder read (Phase 2.4) — replaces store.findRecord.
    const { content: paContent } = (await this.store.request(
      findRecord<PostalAddressModel>('postal-address', addressId)
    )) as { content: { data: PostalAddressModel } };
    account.postalAddress = paContent.data;
    // WarpDrive mutation builder (Phase 2.7) — replaces account.save().
    await saveRecord(this.store, account);
    this.router.refresh('authenticated.reimagined.loans');
  };

  onAccountChanged = () => {
    this.router.refresh('authenticated.reimagined.loans');
  };

  get firstName() {
    return (
      this.sessionContext.user?.person?.firstName || this.intl.t('slr.reimagined.default_name')
    );
  }

  get greeting() {
    const hour = new Date().getHours();
    if (hour < 12) return this.intl.t('slr.reimagined.good_morning');
    if (hour < 17) return this.intl.t('slr.reimagined.good_afternoon');
    return this.intl.t('slr.reimagined.good_evening');
  }

  /**
   * Page state from the SLR alert service — determines which hero variant
   * and which sections are visible on the page.
   *
   * In demo mode, use `?page_state=<state>` to preview.
   */
  get pageState(): SlrPageState | undefined {
    if (this.isDemoMode) {
      const params = new URLSearchParams(window.location.search);
      const param = params.get('page_state');
      if (param) return normalizeSlrPageState(param) ?? 'active';
      // The populated view is gated by pageState BEFORE hasLinkedLoans, so a
      // 'not-linked' debt level must drive the no-accounts state — otherwise the
      // page renders a blank main outlet (no linked loans + active state).
      return getCurrentDemoDebtLevel().hasLinkedLoans ? 'active' : 'no-accounts';
    }
    return this.args.model.contributionHistory?.pageState;
  }

  /**
   * Whether contributions are blocked (limit reached, PIF, or receiving-account
   * action items). NOT tio-payment's employee-blended paymentValidationErrors —
   * those can describe accounts other than the receiving one and carry no
   * on-page explanation (#10022). The /slr-alerts outage notice still renders
   * in the attention section but never blocks — an outage means the status is
   * unknown, not that the contribution can't be processed.
   */
  get isContributionBlocked() {
    const ps = this.pageState;
    return (
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      this.actionItems.some((item) => item.id !== SLR_ALERTS_UNAVAILABLE_ACTION_ID)
    );
  }

  /** Whether to show loan accounts section */
  get showLoanAccounts() {
    const ps = this.pageState;
    return (
      ps === 'active' ||
      ps === undefined ||
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      ps === 'non-slr-former' ||
      ps === 'waiting-period'
    );
  }

  /** Whether to show contribution history section */
  get showContributionHistory() {
    const ps = this.pageState;
    return (
      ps === 'active' ||
      ps === undefined ||
      ps === 'all-pif' ||
      ps === 'limit-annual' ||
      ps === 'limit-lifetime' ||
      ps === 'limit-annual-and-lifetime' ||
      ps === 'non-slr-former' ||
      ps === 'waiting-period'
    );
  }

  /** Whether to show action items (only in active state or when alerts exist) */
  get showActionItems() {
    const ps = this.pageState;
    return ps === 'active' || ps === undefined || this.actionItems.length > 0;
  }

  /** Info banner content for blocked states (PIF, limit reached) */
  get pageStateInfoBanner(): {
    title: string;
    description: string;
    icon: 'check' | 'prohibit';
  } | null {
    const ps = this.pageState;
    if (ps === 'all-pif') {
      return {
        title: this.intl.t('slr.reimagined.page_state_all_pif_title'),
        description: this.intl.t('slr.reimagined.page_state_all_pif_desc'),
        icon: 'check',
      };
    }
    if (ps === 'limit-annual') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_annual_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_annual_desc'),
        icon: 'prohibit',
      };
    }
    if (ps === 'limit-lifetime') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_lifetime_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_lifetime_desc'),
        icon: 'prohibit',
      };
    }
    if (ps === 'limit-annual-and-lifetime') {
      return {
        title: this.intl.t('slr.reimagined.page_state_limit_both_title'),
        description: this.intl.t('slr.reimagined.page_state_limit_both_desc'),
        icon: 'prohibit',
      };
    }
    return null;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="w-full min-w-0">
      {{#if (eq this.pageState "non-slr-never")}}
        {{! NON-SLR USER — never had SLR }}
        <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
          <Heading @level={{1}} class="app-page-title mb-2">
            {{t "slr.reimagined.page_state_not_available_title"}}
          </Heading>
          <Text class="tio-fg-secondary">
            {{t "slr.reimagined.page_state_not_available_desc"}}
          </Text>
        </div>

      {{else if (eq this.pageState "no-accounts")}}
        {{! NO ACCOUNTS — eligible user, needs to add loan account }}
        <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
          <div class="tio-row-start-between mb-6">
            <Heading @level={{1}} class="app-page-title">
              {{t "slr.reimagined.page_state_not_available_title"}}
            </Heading>
            <button
              type="button"
              class="tio-text-base-semibold tio-fg-primary hover:tio-fg-brand underline decoration-solid"
              {{on "click" this.openBenefitDrawer}}
            >
              {{t "slr.reimagined.benefit_details_link"}}
            </button>
          </div>
          <div class="tio-row-start-between">
            <div class="tio-col-loose flex-1">
              <Text class="tio-fg-secondary max-w-lg">
                {{t "slr.reimagined.page_state_no_accounts_desc"}}
              </Text>
              <div>
                <LinkTo
                  @route="authenticated.observability.upload"
                  class="tio-loans-empty-state__cta"
                >
                  {{t "slr.reimagined.page_state_no_accounts_cta"}}
                </LinkTo>
              </div>
            </div>
            <div class="app-stat-card app-stat-card--on-hero tio-col w-60 shrink-0">
              <Text @weight="medium" class="tio-fg-secondary">
                {{t "slr.reimagined.page_state_eligible_for"}}
              </Text>
              <div class="flex items-baseline gap-0.5">
                <span class="text-3xl font-semibold tio-fg-primary">
                  {{formatCurrency this.monthlyContribution}}
                </span>
                <span class="text-base tio-fg-secondary">/month</span>
              </div>
              <Text @size="sm" class="tio-fg-muted">
                {{t
                  "slr.reimagined.page_state_eligible_annually"
                  amount=(formatCurrency this.annualContribution)
                }}
              </Text>
            </div>
          </div>
        </div>

      {{else if this.showLoans}}
        {{#if this.hasLinkedLoans}}
          {{! Hero Section — varies by page state }}
          {{#if (eq this.pageState "non-slr-former")}}
            <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
              <Heading @level={{1}} class="app-page-title mb-2">
                {{t "slr.reimagined.page_state_not_available_title"}}
              </Heading>
              <Text class="tio-fg-secondary">
                {{t "slr.reimagined.page_state_not_available_desc"}}
              </Text>
            </div>
          {{else if (eq this.pageState "waiting-period")}}
            <div class="tio-gradient-brand-soft rounded-3xl p-6 overflow-hidden">
              <div class="tio-row-start-between mb-1">
                <div class="tio-row-tight">
                  <PhMoneyWavy @size={{18}} class="tio-fg-secondary" />
                  <Text @size="sm" class="tio-fg-secondary">
                    {{this.greeting}},
                    {{this.firstName}}
                  </Text>
                </div>
                <button
                  type="button"
                  class="tio-text-base-semibold tio-fg-primary hover:tio-fg-brand underline decoration-solid"
                  {{on "click" this.openBenefitDrawer}}
                >
                  {{t "slr.reimagined.benefit_details_link"}}
                </button>
              </div>
              <Heading @level={{1}} class="app-page-title mb-4">
                {{t "slr.reimagined.page_state_waiting_title"}}
              </Heading>
              <div class="tio-row-start-between gap-4">
                <div class="tio-bg-surface border tio-border-default rounded-2xl p-4 flex-1">
                  <Heading @level={{3}} class="mb-1">
                    {{t
                      "slr.reimagined.page_state_waiting_eligibility"
                      date=this.waitingPeriodDate
                    }}
                  </Heading>
                  <Text @size="sm" class="tio-fg-secondary mb-3">
                    {{t "slr.reimagined.page_state_waiting_desc"}}
                  </Text>
                  <LinkTo
                    @route="authenticated.observability.upload"
                    class="tio-loans-empty-state__cta"
                  >
                    {{t "slr.reimagined.page_state_no_accounts_cta"}}
                  </LinkTo>
                </div>
                <div class="app-stat-card app-stat-card--on-hero tio-col w-60 shrink-0">
                  <Text @weight="medium" class="tio-fg-secondary">
                    {{t "slr.reimagined.page_state_soon_eligible"}}
                  </Text>
                  <div class="flex items-baseline gap-0.5">
                    <span class="text-3xl font-semibold tio-fg-primary">
                      {{formatCurrency this.monthlyContribution}}
                    </span>
                    <span class="text-base tio-fg-secondary">/month</span>
                  </div>
                  <Text @size="sm" class="tio-fg-muted">
                    {{t "slr.reimagined.page_state_starting_date" date=this.waitingPeriodDate}}
                  </Text>
                </div>
              </div>
            </div>
          {{else}}
            <DataIndicator
              @source={{this.contributionsSource}}
              @showBadge={{true}}
              @dataSourceId="slr-contributions"
            >
              <ContributionHero
                @greeting={{this.greeting}}
                @firstName={{this.firstName}}
                @variant={{this.heroProps.variant}}
                @annualUsage={{this.heroProps.annualUsage}}
                @annualLimit={{this.heroProps.annualLimit}}
                @annualProgressPercent={{this.heroProps.annualProgressPercent}}
                @lifetimeUsage={{this.heroProps.lifetimeUsage}}
                @lifetimeLimit={{this.heroProps.lifetimeLimit}}
                @lifetimeProgressPercent={{this.heroProps.lifetimeProgressPercent}}
                @lifetimeContribution={{this.heroProps.lifetimeContribution}}
                @lifetimeAsOfDate={{this.heroProps.lifetimeAsOfDate}}
                @currentYear={{this.heroProps.currentYear}}
                @yearContribution={{this.heroProps.yearContribution}}
                @paymentCount={{this.heroProps.paymentCount}}
                @lastPaymentAmount={{this.heroProps.lastPaymentAmount}}
                @lastPaymentDate={{this.heroProps.lastPaymentDate}}
                @nextContribution={{this.heroProps.nextContribution}}
                @nextContributionDate={{this.heroProps.nextContributionDate}}
                @blocked={{this.heroProps.blocked}}
                @onViewBenefit={{this.openBenefitDrawer}}
              />
            </DataIndicator>
          {{/if}}

          {{! Info Banner for blocked states (PIF, limit reached) }}
          {{#if this.pageStateInfoBanner}}
            <div
              class="mt-6 tio-bg-warning-soft border tio-border-warning rounded-2xl p-5 tio-row-top-loose"
            >
              <div class="p-2 tio-bg-warning-soft rounded-lg shrink-0">
                {{#if (eq this.pageStateInfoBanner.icon "check")}}
                  <PhCheckCircle @size={{24}} class="tio-fg-warning" />
                {{else}}
                  <PhProhibit @size={{24}} class="tio-fg-warning" />
                {{/if}}
              </div>
              <div>
                <Heading @level={{3}} class="tio-fg-warning-body">
                  {{this.pageStateInfoBanner.title}}
                </Heading>
                <Text @size="sm" class="tio-fg-warning-body mt-1">
                  {{this.pageStateInfoBanner.description}}
                </Text>
              </div>
            </div>
          {{/if}}

          {{! Action Items — Needs Your Attention }}
          {{#if this.showActionItems}}
            {{#if this.actionItems.length}}
              <div class="mt-6 tio-col">
                <div class="tio-row-tight px-1">
                  <Heading @level={{3}} class="leading-none">
                    {{t "slr.reimagined.needs_your_attention"}}
                  </Heading>
                  <Text @weight="medium" class="tio-fg-secondary">
                    ({{this.actionItems.length}})
                  </Text>
                </div>

                <div class="tio-col-tight">
                  {{#each this.actionItems as |item|}}
                    {{#if item.tasks}}
                      <GroupedIssueNotificationCard
                        @item={{item}}
                        @person={{this.person}}
                        @onAddressSubmit={{this.handleAddressSubmit}}
                      />
                    {{else if (eq item.resolutionType "upload")}}
                      {{! SLR alerts requiring document upload (blank-account-number, no-loan-balance, blank-payment-address, all-pif) }}
                      <IssueNotificationCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @person={{this.person}}
                        @successMessage={{or
                          item.successMessage
                          (t "slr.reimagined.successfully_added_loan_account")
                        }}
                      />
                    {{else if (eq item.resolutionType "contact-support")}}
                      {{! SLR alerts requiring support contact (partially-applied) }}
                      <div
                        class="tio-bg-surface border tio-border-default rounded-2xl px-4 py-6 text-left w-full"
                      >
                        <div class="tio-row-loose justify-between">
                          <div class="tio-row">
                            <div
                              class="tio-row-center rounded-lg shrink-0 size-12
                                {{this.getActionItemIconBg item}}"
                            >
                              <PhWarningCircle
                                @size={{32}}
                                class={{this.getActionItemIconColor item}}
                              />
                            </div>
                            <div class="tio-col-extra-tight">
                              <Heading
                                @level={{4}}
                                class="tio-fg-secondary capitalize tracking-tight"
                              >
                                {{item.title}}
                              </Heading>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{item.description}}
                              </Text>
                            </div>
                          </div>
                          <LinkTo
                            @route="authenticated.contact-us"
                            class="tio-text-sm-medium tio-fg-info hover:tio-fg-info-body underline whitespace-nowrap shrink-0"
                          >
                            {{t "slr.reimagined.contact_support_link"}}
                          </LinkTo>
                        </div>
                      </div>
                    {{else if (eq item.resolutionType "dropdown")}}
                      {{! SLR alerts requiring address selection (blank-payment-address with MANUAL servicer) }}
                      <AddressSelectionCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @addresses={{this.getAddressOptions item}}
                        @onSubmit={{fn this.handleAddressSubmit item}}
                        data-test-blank-payment-address-card
                      />
                    {{else if (eq item.issueType "account-ineligible")}}
                      <IssueNotificationCard
                        @title={{item.title}}
                        @description={{item.description}}
                        @person={{this.person}}
                        @successMessage={{or
                          item.successMessage
                          (t "slr.reimagined.successfully_added_loan_account")
                        }}
                        data-test-account-ineligible-card
                      />
                    {{else if (eq item.issueType "account-setup")}}
                      <button
                        type="button"
                        class="tio-bg-surface border tio-border-default rounded-2xl px-4 py-6 text-left w-full cursor-pointer hover:tio-border-strong hover:shadow-sm transition-all"
                        {{on "click" (fn this.handleActionItemClick item)}}
                      >
                        <div class="tio-row-between">
                          <div class="tio-row">
                            <div
                              class="tio-row-center rounded-lg shrink-0 size-12
                                {{this.getActionItemIconBg item}}"
                            >
                              <PhLinkBreak
                                @size={{32}}
                                class={{this.getActionItemIconColor item}}
                              />
                            </div>
                            <div class="tio-col-extra-tight">
                              <div class="tio-row-tight">
                                <Heading
                                  @level={{4}}
                                  class="tio-fg-secondary capitalize tracking-tight"
                                >
                                  {{item.title}}
                                </Heading>
                                {{#if item.badge}}
                                  <Chip @intent={{item.badgeIntent}} @size="sm">
                                    {{item.badge}}
                                  </Chip>
                                {{/if}}
                              </div>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{item.description}}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </button>
                    {{else}}
                      <div
                        class="tio-bg-surface border tio-border-default rounded-2xl px-4 py-6 text-left w-full"
                      >
                        <div class="tio-row-between">
                          <div class="tio-row">
                            <div
                              class="tio-row-center rounded-lg shrink-0 size-12
                                {{this.getActionItemIconBg item}}"
                            >
                              {{#if (eq item.issueType "plan-requirement")}}
                                <PhShieldSlash
                                  @size={{32}}
                                  class={{this.getActionItemIconColor item}}
                                />
                              {{else}}
                                <PhWarningCircle
                                  @size={{32}}
                                  class={{this.getActionItemIconColor item}}
                                />
                              {{/if}}
                            </div>
                            <div class="tio-col-extra-tight">
                              <div class="tio-row-tight">
                                <Heading
                                  @level={{4}}
                                  class="tio-fg-secondary capitalize tracking-tight"
                                >
                                  {{item.title}}
                                </Heading>
                                {{#if item.badge}}
                                  <Chip @intent={{item.badgeIntent}} @size="sm">
                                    {{item.badge}}
                                  </Chip>
                                {{/if}}
                              </div>
                              <Text @size="sm" @weight="medium" class="tio-fg-muted">
                                {{item.description}}
                              </Text>
                            </div>
                          </div>
                        </div>
                      </div>
                    {{/if}}
                  {{/each}}
                </div>
              </div>
            {{/if}}
          {{/if}}
        {{/if}}
      {{/if}}

      <AccountSelectorDrawer
        @isOpen={{this.isAccountDrawerOpen}}
        @onClose={{this.toggleAccountDrawer}}
        @accounts={{@model.activeAccounts}}
        @currentAccount={{@model.accountReceivingContributions}}
        @onAccountChanged={{this.onAccountChanged}}
        @personId={{@model.personId}}
      />

      <DocumentUploadDrawer
        @isOpen={{this.isUploadDrawerOpen}}
        @onClose={{this.toggleUploadDrawer}}
        @person={{this.person}}
      />

      <BenefitDetailDrawer
        @isOpen={{this.isBenefitDrawerOpen}}
        @onClose={{this.closeBenefitDrawer}}
        @plan={{this.plan}}
        @limitsStatus={{@model.limitsStatus}}
      />
    </VStack>
  </template>
}
